import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'

import { setElementStyles } from 'builder/components/JoyRide/utils'
import { BuilderTourParams, actions as uiActions } from 'builder/modules/ui'
import { actions, SectionNames } from 'builder/modules/resumeEditor'
import { actions as userActions } from 'builder/modules/user'
import { actions as panelActions } from 'builder/modules/panel'

import { useUser } from 'builder/hooks/useUser'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { DesignTourStepIndex, EditorTourStepIndex, StepName } from '../types'
import { fadeOutStyles, isPhrasesSuggestionsStepPresent } from '../utils'

export const useTourConfig = () => {
  const user = useUser()
  const dispatch = useDispatch()
  const { isPhone, isTablet } = useMediaQueries()
  const builderTour = useTypedSelector(state => state.ui.builderTour)
  const isTourEnabled = false
  const isTourCompleted = user?.uiStates?.builderTourCompleted
  const isTourRunning = builderTour.isRunning && isTourEnabled && !isTourCompleted
  const isStartModalOpen = builderTour.isStartModalOpen && isTourEnabled && !isTourCompleted
  const canResumeTour = isTourEnabled && !isTourCompleted && builderTour.isTourActive

  const setTourCompleted = () => {
    if (isTourCompleted) return
    dispatch(panelActions.setUIStatesConfigStatus({ builder_tour_completed: true }))
    dispatch(userActions.setUIUserState({ builderTourCompleted: true }))
  }

  const toggleAllSections = (open: boolean) => {
    const sections = Object.values(SectionNames).reduce((acc, sectionName) => {
      acc[sectionName] = open
      return acc
    }, {} as Record<SectionNames, boolean>)

    dispatch(actions.setOpenedSections(sections))
  }

  const toggleTour = ({ showSkipButton = true, ...rest }: BuilderTourParams) => {
    if (!isTourEnabled || isTourCompleted) return
    dispatch(uiActions.toggleBuilderTour({ ...rest, showSkipButton }))
  }

  const setAnimationStyles = () => {
    requestAnimationFrame(() => {
      setElementStyles('.__floater', fadeOutStyles)
      setElementStyles('.react-joyride__overlay', fadeOutStyles)
    })
  }

  const toggleDelayedTour = (
    stepIndex: EditorTourStepIndex | DesignTourStepIndex,
    stepName: StepName,
  ) => {
    setTimeout(() => {
      toggleTour({
        isRunning: true,
        isStartModalOpen: false,
        stepIndex,
        stepName,
      })
    }, 500)
  }

  const onEndTour = () => {
    trackInternalEvent('rb_onboarding_completed')
    toggleTour({
      isRunning: false,
      isStartModalOpen: false,
      stepIndex: 0,
      showSkipButton: false,
      isTourActive: false,
    })
    setTourCompleted()
  }

  const skipTourStep = (
    stepName: StepName,
    stepIndex?: EditorTourStepIndex | DesignTourStepIndex,
  ) => {
    if (!isTourEnabled || isTourCompleted) return
    toggleTour({ isRunning: false, isStartModalOpen: false, stepIndex: stepIndex ?? 0, stepName })
  }

  const skipAll = () => {
    if (!isTourEnabled || isTourCompleted) return
    trackInternalEvent('skip_rb_onboarding')
    toggleTour({
      isRunning: false,
      isStartModalOpen: false,
      stepIndex: 0,
      stepName: 'design',
      showSkipButton: false,
      isTourActive: false,
    })
    setTourCompleted()
  }

  const selectTemplate = (showColor: boolean) => {
    if (!canResumeTour) return
    const stepIndex = showColor ? DesignTourStepIndex.SELECT_COLOR : EditorTourStepIndex.EDIT_RESUME
    const stepName = showColor ? 'design' : 'editor'
    toggleDelayedTour(stepIndex, stepName)
  }

  const onUpdateDragSections = () => {
    if (!canResumeTour) return
    setAnimationStyles()
    toggleTour({ isRunning: false, isStartModalOpen: false, stepIndex: 0, stepName: 'editor' })
    toggleAllSections(true)
    setTimeout(() => {
      if (isPhrasesSuggestionsStepPresent()) {
        toggleDelayedTour(EditorTourStepIndex.PHRASES_SUGGESTIONS, 'editor')
      } else {
        toggleDelayedTour(EditorTourStepIndex.DOWNLOAD_RESUME, 'download')
      }
    }, 100)
  }

  const onChangePhrasesSuggestions = () => {
    if (!canResumeTour) return
    setAnimationStyles()
    toggleTour({ isRunning: false, isStartModalOpen: false, stepIndex: 0, stepName: 'download' })
    toggleDelayedTour(EditorTourStepIndex.DOWNLOAD_RESUME, 'download')
  }

  const startInitTourModal = () => {
    if (!isTourEnabled || isTourCompleted) return
    const panelType = isTablet || isPhone ? 'customization' : 'editor'
    dispatch(actions.setEditorPanelType(panelType))
    toggleTour({
      isRunning: false,
      isStartModalOpen: true,
      stepIndex: 0,
      stepName: 'design',
      isTourActive: true,
    })
  }

  return {
    isTourRunning,
    isStartModalOpen,
    isTourEnabled,
    isTourCompleted,
    stepName: builderTour.stepName,
    stepIndex: builderTour.stepIndex,
    showSkipButton: builderTour.showSkipButton,
    skipAll,
    onEndTour,
    toggleTour,
    skipTourStep,
    selectTemplate,
    setTourCompleted,
    toggleAllSections,
    setAnimationStyles,
    toggleDelayedTour,
    startInitTourModal,
    onUpdateDragSections,
    onChangePhrasesSuggestions,
  }
}
